import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { getEgsBeddingLabel, getEgsRoomLabel } from './egsModel';
import { EGS_COLLECTED_BY_LABELS, EGS_GUEST_TYPE_LABELS, EGS_RATE_TYPE_LABELS } from './fields';
import type { EgsDraft } from './types';

interface Props {
  drafts: Array<EgsDraft>;
  onEdit: (draft: EgsDraft) => void;
  onRemove: (id: string) => void;
}

export default function EgsTable({ drafts, onEdit, onRemove }: Props) {
  if (!drafts.length) {
    return (
      <Typography color="text.secondary" fontStyle="italic" variant="body2">
        No extra guest surcharges added yet.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Room Name</TableCell>
            <TableCell>Extra Guest Type</TableCell>
            <TableCell>Age Range</TableCell>
            <TableCell>Collected By</TableCell>
            <TableCell>Rate Type</TableCell>
            <TableCell>Bedding Type</TableCell>
            <TableCell>Inclusions</TableCell>
            <TableCell align="right">Cost Price</TableCell>
            <TableCell align="right">Converted Cost</TableCell>
            <TableCell align="right">Sell Price (AUD)</TableCell>
            <TableCell align="right">% Margin</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.map((draft) => (
            <TableRow key={draft.id}>
              <TableCell>
                <Stack spacing={0.5}>
                  <span>{getEgsRoomLabel(draft)}</span>
                  {draft.isAdditionalNightlyCost && draft.packageOptions?.length ? (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                      {draft.packageOptions.map((option) => (
                        <Chip key={option.id} label={option.label} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell>{EGS_GUEST_TYPE_LABELS[draft.guestType]}</TableCell>
              <TableCell>{`${draft.fromAge} - ${draft.toAge}`}</TableCell>
              <TableCell>{EGS_COLLECTED_BY_LABELS[draft.collectedBy]}</TableCell>
              <TableCell>{draft.rateType ? EGS_RATE_TYPE_LABELS[draft.rateType] : '—'}</TableCell>
              <TableCell>{getEgsBeddingLabel(draft)}</TableCell>
              <TableCell>{draft.inclusions}</TableCell>
              <TableCell align="right">{draft.costPrice.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.convertedCost.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.sellPriceAud.toFixed(2)}</TableCell>
              <TableCell align="right">{draft.marginPct != null ? `${draft.marginPct.toFixed(2)}%` : '—'}</TableCell>
              <TableCell>
                {draft.isAdditionalNightlyCost ? (
                  <Tooltip title="Edit the base row instead - this one is generated from it">
                    <span>
                      <IconButton aria-label="Edit" disabled size="small">
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <IconButton aria-label="Edit" onClick={() => onEdit(draft)} size="small">
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton aria-label="Delete" onClick={() => onRemove(draft.id)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
